import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Table, Tag, Input, Select, Button, Modal, DatePicker, InputNumber, message, Upload, Icon, Steps, Alert, Dropdown, Menu, Popover, Badge, Checkbox } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import 'moment/locale/pt-br';

import * as dividasActions from './actions';
import { ModuleHeader } from '~/components/ModuleHeader';
import { getDefaultPaginationConfig } from '~/utils/get-default-pagination-config';
import { STORAGE } from '~/constants/storage.constants';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import { countActiveDividasFilters } from './helpers';

var Dragger = Upload.Dragger;
var Column = Table.Column;
var Step = Steps.Step;

var DraggerStyled = styled(Dragger)`
    width: 100% !important;
    padding: 10px !important;
    min-height: 120px !important;
    margin-bottom: 10px !important;

    .ant-upload-drag-icon {
        margin-bottom: 8px !important;
    }

    .ant-upload-drag-icon .anticon {
        font-size: 32px !important;
        color: #1890ff !important;
    }

    .ant-upload-text {
        font-size: 14px !important;
        color: #666 !important;
    }

    .ant-upload-hint {
        font-size: 12px !important;
        color: #999 !important;
    }
`;

var Option = Select.Option;

var SkeletonLine = styled.div`
    height: 14px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 2px;

    @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
`;

var SCOPE_OPTIONS = [
    { value: 'federal', label: 'FEDERAL' },
    { value: 'estadual', label: 'ESTADUAL' },
    { value: 'municipal', label: 'MUNICIPAL' },
    { value: 'inss', label: 'INSS' },
    { value: 'fgts', label: 'FGTS' },
    { value: 'dirpf', label: 'DIRPF' }
];

var SCOPE_COLORS = {
    federal: 'blue',
    estadual: 'green',
    municipal: 'orange',
    inss: 'purple',
    fgts: 'cyan',
    dirpf: 'magenta'
};

function hasValue(value) {
    return Number(value) > 0;
}

function formatCurrency(value) {
    if (!hasValue(value)) return '-';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
}

function List(props) {
    var { data, isLoading, createDivida, updateDivida, deleteDivida, archiveDivida, unarchiveDivida, onRefresh, customers, showArchived, onToggleArchived } = props;

    // Applied filters live in the screen (usePersistedFilters), which reloads
    // the list when "arquivadas" changes; âmbito and busca are client-side.
    var persisted = props.persisted;
    var defaults = persisted.defaults;
    var search = props.applied.search;
    var scopeFilter = props.applied.scopeFilter;
    var setSearch = function(value) { persisted.setFilters({ search: value }); };
    var setScopeFilter = function(value) { persisted.setFilters({ scopeFilter: value }); };
    var [isModalVisible, setIsModalVisible] = useState(false);
    var [editingRecord, setEditingRecord] = useState(null);
    var [fileList, setFileList] = useState([]);
    var [currentStep, setCurrentStep] = useState(0);
    var [archiveModalVisible, setArchiveModalVisible] = useState(false);
    var [archiveRecord, setArchiveRecord] = useState(null);
    var [archiveNote, setArchiveNote] = useState('');
    var [formData, setFormData] = useState({
        customer_id: null,
        notification_date: null,
        scope: 'federal',
        value: 0,
        notes: '',
        notify_customer: true
    });

    var skeletonData = [1, 2, 3, 4, 5, 6, 7, 8].map(function(i) {
        return { id: 'skeleton-' + i, isSkeletonRow: true };
    });

    var filtered = data.filter(function(item) {
        var customerName = item.customer && item.customer.name ? item.customer.name.toLowerCase() : '';
        var customerCod = item.customer && item.customer.cod ? item.customer.cod.toString().toLowerCase() : '';
        var searchLower = search.toLowerCase();

        var matchesSearch = customerName.indexOf(searchLower) >= 0 || customerCod.indexOf(searchLower) >= 0;
        var matchesScope = !scopeFilter || item.scope === scopeFilter;

        return matchesSearch && matchesScope;
    });

    var tableData = isLoading ? skeletonData : filtered;

    var isDefaultArchived = !showArchived;
    var filterCount = countActiveDividasFilters(props.applied, defaults);

    var getScopeFilterLabel = function() {
        if (!scopeFilter) return null;
        var opt = SCOPE_OPTIONS.find(function(o) { return o.value === scopeFilter; });
        return opt ? opt.label : scopeFilter;
    };

    var filterPills = [];
    filterPills.push({
        key: 'archived',
        label: showArchived ? 'Arquivadas' : 'Ativas',
        color: showArchived ? '#8c8c8c' : '#1890ff',
        icon: showArchived ? 'inbox' : 'check-circle',
        onRemove: !isDefaultArchived ? function() { onToggleArchived(); } : undefined,
    });
    if (scopeFilter) {
        filterPills.push({
            key: 'scope',
            label: 'Âmbito: ' + getScopeFilterLabel(),
            color: SCOPE_COLORS[scopeFilter] || '#1890ff',
            onRemove: function() { setScopeFilter(null); },
        });
    }
    if (search) {
        filterPills.push({
            key: 'search',
            label: 'Busca: ' + search,
            color: '#722ed1',
            icon: 'search',
            onRemove: function() { setSearch(''); },
        });
    }

    // Back to defaults (the screen reloads if "arquivadas" was on).
    var handleClearAllFilters = persisted.resetFilters;

    var restoredSummary = filterPills
        .filter(function(pill) { return !!pill.onRemove; })
        .map(function(pill) { return pill.label; })
        .join(' · ');

    function handleOpenModal(record) {
        if (record) {
            setEditingRecord(record);
            setFormData({
                customer_id: record.customer_id,
                notification_date: record.notification_date,
                scope: record.scope,
                value: record.value,
                notes: record.notes || '',
                notify_customer: false
            });
            setFileList([]);
        } else {
            setEditingRecord(null);
            setFormData({
                customer_id: null,
                notification_date: null,
                scope: 'federal',
                value: 0,
                notes: '',
                notify_customer: true
            });
            setFileList([]);
        }
        setCurrentStep(0);
        setIsModalVisible(true);
    }

    function handleCloseModal() {
        setIsModalVisible(false);
        setEditingRecord(null);
        setCurrentStep(0);
        setFormData({
            customer_id: null,
            notification_date: null,
            scope: 'federal',
            value: 0,
            notes: '',
            notify_customer: true
        });
        setFileList([]);
    }

    function handleNextStep() {
        if (!formData.customer_id) {
            message.error('Selecione um cliente');
            return;
        }
        if (!formData.notification_date) {
            message.error('Informe a data do aviso');
            return;
        }
        setCurrentStep(1);
    }

    function handlePrevStep() {
        setCurrentStep(0);
    }

    function getSelectedCustomer() {
        if (!formData.customer_id) return null;
        return customers.find(function(c) { return c.id === formData.customer_id; });
    }

    function getScopeLabel(scopeValue) {
        var option = SCOPE_OPTIONS.find(function(opt) { return opt.value === scopeValue; });
        return option ? option.label : scopeValue;
    }

    function handleSubmit() {
        var submitFormData = new FormData();
        submitFormData.append('customer_id', formData.customer_id);
        submitFormData.append('notification_date', moment(formData.notification_date).format('YYYY-MM-DD'));
        submitFormData.append('scope', formData.scope);
        submitFormData.append('value', formData.value || 0);
        submitFormData.append('notes', formData.notes || '');
        if (!editingRecord) {
            submitFormData.append('notify_customer', formData.notify_customer ? 1 : 0);
        }

        if (fileList.length > 0) {
            var file = fileList[0].originFileObj || fileList[0];
            submitFormData.append('file', file);
        }

        if (editingRecord) {
            submitFormData.append('_method', 'PUT');
            updateDivida(editingRecord.id, submitFormData).then(function() {
                message.success('Registro atualizado com sucesso!');
                handleCloseModal();
                if (onRefresh) onRefresh();
            }).catch(function() {
                message.error('Erro ao atualizar registro');
            });
        } else {
            createDivida(submitFormData).then(function() {
                message.success('Registro criado com sucesso!');
                handleCloseModal();
                if (onRefresh) onRefresh();
            }).catch(function() {
                message.error('Erro ao criar registro');
            });
        }
    }

    function handleDelete(record) {
        Modal.confirm({
            title: 'Você realmente deseja excluir este registro?',
            content: 'Esta ação não poderá ser desfeita.',
            okText: 'Sim',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: function() {
                deleteDivida(record.id).then(function() {
                    message.success('Registro excluído com sucesso!');
                }).catch(function() {
                    message.error('Erro ao excluir registro');
                });
            }
        });
    }

    function handleOpenArchiveModal(record) {
        setArchiveRecord(record);
        setArchiveNote('');
        setArchiveModalVisible(true);
    }

    function handleCloseArchiveModal() {
        setArchiveModalVisible(false);
        setArchiveRecord(null);
        setArchiveNote('');
    }

    function handleArchive() {
        if (!archiveRecord) return;

        archiveDivida(archiveRecord.id, archiveNote).then(function() {
            message.success('Registro arquivado com sucesso!');
            handleCloseArchiveModal();
            if (onRefresh) onRefresh();
        }).catch(function() {
            message.error('Erro ao arquivar registro');
        });
    }

    function handleUnarchive(record) {
        Modal.confirm({
            title: 'Desarquivar registro?',
            content: 'O registro voltará para a lista principal.',
            okText: 'Sim',
            cancelText: 'Cancelar',
            onOk: function() {
                unarchiveDivida(record.id).then(function() {
                    message.success('Registro desarquivado com sucesso!');
                    if (onRefresh) onRefresh();
                }).catch(function() {
                    message.error('Erro ao desarquivar registro');
                });
            }
        });
    }

    function handleFileChange(info) {
        var file = info.file;

        if (file.status === 'removed') {
            setFileList([]);
            return;
        }

        var isPDF = file.type === 'application/pdf';
        if (!isPDF) {
            message.error('Apenas arquivos PDF são permitidos!');
            return;
        }

        var isLt50M = file.size / 1024 / 1024 < 50;
        if (!isLt50M) {
            message.error('O arquivo deve ter menos de 50MB!');
            return;
        }

        setFileList([file]);
    }

    function beforeUpload() {
        return false;
    }

    return (
        <div>
            <ModuleHeader
                    breadcrumbs={['Compliance', 'Dívidas Tributárias']}
                    title="Dívidas Tributárias"
                    search={
                        <Search
                            value={search}
                            placeholder="Pesquisar pelo nome do cliente"
                            onChange={function(e) { setSearch(e.target.value); }}
                        />
                    }
                    actions={
                        <React.Fragment>
                            <Popover
                                trigger="click"
                                placement="bottomRight"
                                content={
                                    <div style={{ width: 320 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                            <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                                            {!persisted.isDefault && (
                                                <a
                                                    style={{ fontSize: 12, color: '#f5222d' }}
                                                    onClick={handleClearAllFilters}
                                                >
                                                    Limpar tudo
                                                </a>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: 14 }}>
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visualização</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {[
                                                    { value: false, label: 'Ativas', count: data.filter(function(d) { return !d.archived_at; }).length, color: '#1890ff' },
                                                    { value: true, label: 'Arquivadas', count: data.filter(function(d) { return !!d.archived_at; }).length, color: '#8c8c8c', icon: 'inbox' },
                                                ].map(function(opt) {
                                                    var isActive = showArchived === opt.value;
                                                    return (
                                                        <div
                                                            key={String(opt.value)}
                                                            onClick={function() { if (showArchived !== opt.value) onToggleArchived(); }}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                                                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                                                background: isActive ? opt.color + '0a' : '#fafafa',
                                                                transition: 'all 0.2s',
                                                            }}
                                                        >
                                                            <span style={{ fontSize: 12, color: isActive ? '#262626' : '#8c8c8c' }}>
                                                                {opt.icon && <Icon type={opt.icon} style={{ marginRight: 4 }} />}
                                                                {opt.label}
                                                            </span>
                                                            <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Âmbito</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                <div
                                                    onClick={function() { setScopeFilter(null); }}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                                        padding: '4px 10px', borderRadius: 16, cursor: 'pointer', fontSize: 13,
                                                        background: !scopeFilter ? '#1890ff' : '#f5f5f5',
                                                        color: !scopeFilter ? '#fff' : '#595959',
                                                        border: '1px solid ' + (!scopeFilter ? '#1890ff' : '#e8e8e8'),
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    Todos
                                                </div>
                                                {SCOPE_OPTIONS.map(function(opt) {
                                                    var isActive = scopeFilter === opt.value;
                                                    var scopeColor = SCOPE_COLORS[opt.value] || '#1890ff';
                                                    var cnt = data.filter(function(d) { return d.scope === opt.value; }).length;
                                                    return (
                                                        <div
                                                            key={opt.value}
                                                            onClick={function() { setScopeFilter(opt.value); }}
                                                            style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                                                padding: '4px 10px', borderRadius: 16, cursor: 'pointer', fontSize: 13,
                                                                background: isActive ? scopeColor : '#f5f5f5',
                                                                color: isActive ? '#fff' : '#595959',
                                                                border: '1px solid ' + (isActive ? scopeColor : '#e8e8e8'),
                                                                transition: 'all 0.2s',
                                                            }}
                                                        >
                                                            {!isActive && <span style={{ width: 8, height: 8, borderRadius: '50%', background: scopeColor, flexShrink: 0 }} />}
                                                            {isActive && <Icon type="check" style={{ fontSize: 11 }} />}
                                                            {opt.label}
                                                            <span style={{ fontSize: 11, opacity: 0.8 }}>({cnt})</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                }
                            >
                                <Badge
                                    count={filterCount}
                                    offset={[-4, 0]}
                                    style={{ backgroundColor: '#722ed1' }}
                                >
                                    <Button
                                        icon="bars"
                                        style={{ borderRadius: 8 }}
                                    >
                                        Filtros
                                    </Button>
                                </Badge>
                            </Popover>
                            <Dropdown
                                trigger={['click']}
                                overlay={
                                    <Menu>
                                        {!showArchived && (
                                            <Menu.Item key="new" onClick={function() { handleOpenModal(null); }}>
                                                <Icon type="plus" /> Nova Dívida
                                            </Menu.Item>
                                        )}
                                    </Menu>
                                }
                            >
                                <Button icon="plus" shape="circle" />
                            </Dropdown>
                        </React.Fragment>
                    }
                    advanced={filterPills.length > 0 ? (
                        <FilterPillsBar
                            pills={filterPills}
                            onClearAll={!persisted.isDefault ? handleClearAllFilters : undefined}
                        />
                    ) : null}
            />
            <RestoredFiltersNotice
                visible={persisted.wasRestored}
                summary={restoredSummary}
                onReset={handleClearAllFilters}
                onDismiss={persisted.dismissRestored}
            />
            <div className="table-clabs">
                <Table
                    dataSource={tableData}
                    rowKey="id"
                    rowClassName="row-clabs-grid"
                    pagination={getDefaultPaginationConfig({
                        storageKey: STORAGE.defaultPageSize.dividasTributarias || 'dividasTributarias_pageSize',
                        defaultPageSize: 50
                    })}
                    bordered={true}
                    scroll={{ x: true, y: 'calc(100vh - 330px)' }}
                >
                    <Column
                        title="Cliente"
                        dataIndex="customer"
                        key="customer"
                        width={350}
                        render={function(customer, record) {
                            if (record.isSkeletonRow) {
                                return <SkeletonLine style={{ width: 250 }} />;
                            }
                            if (!customer) return '-';
                            return (
                                <span>
                                    <Tag>{customer.cod}</Tag>
                                    {customer.name}
                                </span>
                            );
                        }}
                        sorter={function(a, b) {
                            var nameA = a.customer && a.customer.name ? a.customer.name : '';
                            var nameB = b.customer && b.customer.name ? b.customer.name : '';
                            return nameA.localeCompare(nameB);
                        }}
                    />
                    <Column
                        title="Data do Aviso"
                        dataIndex="notification_date"
                        key="notification_date"
                        width={150}
                        render={function(date, record) {
                            if (record.isSkeletonRow) {
                                return <SkeletonLine style={{ width: 100 }} />;
                            }
                            return date ? moment(date).format('DD/MM/YYYY') : '-';
                        }}
                        sorter={function(a, b) {
                            return moment(a.notification_date).valueOf() - moment(b.notification_date).valueOf();
                        }}
                    />
                    <Column
                        title="Âmbito"
                        dataIndex="scope"
                        key="scope"
                        width={120}
                        render={function(scope, record) {
                            if (record.isSkeletonRow) {
                                return <SkeletonLine style={{ width: 80 }} />;
                            }
                            return (
                                <Tag color={SCOPE_COLORS[scope]}>{getScopeLabel(scope)}</Tag>
                            );
                        }}
                    />
                    <Column
                        title="Arquivo"
                        dataIndex="document_url"
                        key="document_url"
                        width={100}
                        align="center"
                        render={function(url, record) {
                            if (record.isSkeletonRow) {
                                return <SkeletonLine style={{ width: 32 }} />;
                            }
                            if (!url) return '-';
                            return (
                                <Button
                                    icon="file-pdf"
                                    href={url}
                                    target="_blank"
                                    size="small"
                                />
                            );
                        }}
                    />
                    <Column
                        title="Valor"
                        dataIndex="value"
                        key="value"
                        width={150}
                        align="right"
                        render={function(value, record) {
                            if (record.isSkeletonRow) {
                                return <SkeletonLine style={{ width: 80 }} />;
                            }
                            return formatCurrency(value);
                        }}
                        sorter={function(a, b) {
                            return (a.value || 0) - (b.value || 0);
                        }}
                    />
                    <Column
                        title="Ações"
                        key="actions"
                        width={80}
                        align="center"
                        render={function(_, record) {
                            if (record.isSkeletonRow) {
                                return <SkeletonLine style={{ width: 32 }} />;
                            }
                            var menu = showArchived ? (
                                <Menu>
                                    <Menu.Item key="unarchive" onClick={function() { handleUnarchive(record); }}>
                                        <Icon type="undo" style={{ marginRight: 8 }} />
                                        Desarquivar
                                    </Menu.Item>
                                </Menu>
                            ) : (
                                <Menu>
                                    <Menu.Item key="edit" onClick={function() { handleOpenModal(record); }}>
                                        <Icon type="edit" style={{ marginRight: 8 }} />
                                        Editar
                                    </Menu.Item>
                                    <Menu.Item key="archive" onClick={function() { handleOpenArchiveModal(record); }}>
                                        <Icon type="inbox" style={{ marginRight: 8 }} />
                                        Arquivar
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item key="delete" onClick={function() { handleDelete(record); }} style={{ color: '#ff4d4f' }}>
                                        <Icon type="delete" style={{ marginRight: 8 }} />
                                        Excluir
                                    </Menu.Item>
                                </Menu>
                            );
                            return (
                                <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                                    <Button icon="more" size="small" />
                                </Dropdown>
                            );
                        }}
                    />
                </Table>
            </div>

            <Modal
                title={editingRecord ? 'Editar Dívida Tributária' : 'Nova Dívida Tributária'}
                visible={isModalVisible}
                onCancel={handleCloseModal}
                width={600}
                footer={
                    currentStep === 0 ? (
                        <div>
                            <Button onClick={handleCloseModal}>Cancelar</Button>
                            <Button type="primary" onClick={handleNextStep}>Próximo</Button>
                        </div>
                    ) : (
                        <div>
                            <Button onClick={handlePrevStep}>Voltar</Button>
                            <Button type="primary" onClick={handleSubmit}>Salvar</Button>
                        </div>
                    )
                }
            >
                <Steps current={currentStep} style={{ marginBottom: 24 }}>
                    <Step title="Preencher Dados" />
                    <Step title="Revisar" />
                </Steps>

                {currentStep === 0 && (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <label>Cliente</label>
                            <Select
                                showSearch
                                style={{ width: '100%' }}
                                placeholder="Selecione um cliente"
                                value={formData.customer_id}
                                onChange={function(value) {
                                    setFormData(Object.assign({}, formData, { customer_id: value }));
                                }}
                                filterOption={function(input, option) {
                                    return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                }}
                            >
                                {customers.map(function(customer) {
                                    return (
                                        <Option key={customer.id} value={customer.id}>
                                            {customer.cod + ' - ' + customer.name}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label>Data do Aviso</label>
                            <DatePicker
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                value={formData.notification_date ? moment(formData.notification_date) : null}
                                onChange={function(date) {
                                    setFormData(Object.assign({}, formData, { notification_date: date }));
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label>Âmbito</label>
                            <Select
                                style={{ width: '100%' }}
                                value={formData.scope}
                                onChange={function(value) {
                                    setFormData(Object.assign({}, formData, { scope: value }));
                                }}
                            >
                                {SCOPE_OPTIONS.map(function(opt) {
                                    return <Option key={opt.value} value={opt.value}>{opt.label}</Option>;
                                })}
                            </Select>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label>Documento (PDF)</label>
                            <DraggerStyled
                                fileList={fileList}
                                onChange={handleFileChange}
                                beforeUpload={beforeUpload}
                                accept=".pdf"
                                showUploadList={true}
                            >
                                <p className="ant-upload-drag-icon">
                                    <Icon type="inbox" />
                                </p>
                                <p className="ant-upload-text">
                                    Clique ou arraste o arquivo PDF aqui
                                </p>
                                <p className="ant-upload-hint">
                                    Apenas arquivos PDF (máx. 50MB)
                                </p>
                            </DraggerStyled>
                            {editingRecord && editingRecord.document_url && fileList.length === 0 && (
                                <div style={{ marginTop: 8, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
                                    <a href={editingRecord.document_url} target="_blank" rel="noopener noreferrer">
                                        <Icon type="file-pdf" style={{ marginRight: 8, color: '#ff4d4f' }} />
                                        Ver documento atual
                                    </a>
                                </div>
                            )}
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label>Valor (R$)</label>
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={0.01}
                                precision={2}
                                decimalSeparator=","
                                formatter={function(value) {
                                    return value ? value.toString().replace('.', ',') : '';
                                }}
                                parser={function(value) {
                                    return value ? parseFloat(value.replace(',', '.')) : 0;
                                }}
                                value={formData.value}
                                onChange={function(value) {
                                    setFormData(Object.assign({}, formData, { value: value || 0 }));
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label>Observações</label>
                            <Input.TextArea
                                rows={3}
                                placeholder="Observações adicionais..."
                                value={formData.notes}
                                onChange={function(e) {
                                    setFormData(Object.assign({}, formData, { notes: e.target.value }));
                                }}
                            />
                        </div>
                    </div>
                )}

                {currentStep === 1 && (
                    <div>
                        <Alert
                            message="Informação"
                            description="Esta informação ficará disponível no portal do cliente após salvar. Nenhuma notificação será enviada automaticamente."
                            type="info"
                            showIcon
                            style={{ marginBottom: 24 }}
                        />

                        <div style={{ background: '#fafafa', padding: 16, borderRadius: 4 }}>
                            <h4 style={{ marginBottom: 16, borderBottom: '1px solid #e8e8e8', paddingBottom: 8 }}>
                                Confirme os dados antes de salvar:
                            </h4>

                            <div style={{ marginBottom: 12 }}>
                                <strong>Cliente:</strong>
                                <div style={{ marginTop: 4 }}>
                                    {getSelectedCustomer() ? (
                                        <span>
                                            <Tag>{getSelectedCustomer().cod}</Tag>
                                            {getSelectedCustomer().name}
                                        </span>
                                    ) : '-'}
                                </div>
                            </div>

                            <div style={{ marginBottom: 12 }}>
                                <strong>Data do Aviso:</strong>
                                <div style={{ marginTop: 4 }}>
                                    {formData.notification_date ? moment(formData.notification_date).format('DD/MM/YYYY') : '-'}
                                </div>
                            </div>

                            <div style={{ marginBottom: 12 }}>
                                <strong>Âmbito:</strong>
                                <div style={{ marginTop: 4 }}>
                                    <Tag color={SCOPE_COLORS[formData.scope]}>{getScopeLabel(formData.scope)}</Tag>
                                </div>
                            </div>

                            <div style={{ marginBottom: 12 }}>
                                <strong>Documento:</strong>
                                <div style={{ marginTop: 4 }}>
                                    {fileList.length > 0 ? (
                                        <span>
                                            <Icon type="file-pdf" style={{ marginRight: 8, color: '#ff4d4f' }} />
                                            {fileList[0].name || 'Novo arquivo selecionado'}
                                        </span>
                                    ) : editingRecord && editingRecord.document_url ? (
                                        <span>
                                            <Icon type="file-pdf" style={{ marginRight: 8, color: '#ff4d4f' }} />
                                            Documento atual mantido
                                        </span>
                                    ) : (
                                        <span style={{ color: '#999' }}>Nenhum documento</span>
                                    )}
                                </div>
                            </div>

                            {hasValue(formData.value) && (
                                <div style={{ marginBottom: 12 }}>
                                    <strong>Valor:</strong>
                                    <div style={{ marginTop: 4, fontSize: 16, color: '#1890ff' }}>
                                        {formatCurrency(formData.value)}
                                    </div>
                                </div>
                            )}

                            {formData.notes && (
                                <div style={{ marginBottom: 12 }}>
                                    <strong>Observações:</strong>
                                    <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
                                        {formData.notes}
                                    </div>
                                </div>
                            )}
                        </div>

                        {!editingRecord && (
                            <div style={{ marginTop: 16 }}>
                                <Checkbox
                                    checked={formData.notify_customer}
                                    onChange={function(e) {
                                        setFormData(Object.assign({}, formData, { notify_customer: e.target.checked }));
                                    }}
                                >
                                    Avisar o cliente por e-mail
                                </Checkbox>
                                <div style={{ marginTop: 4, marginLeft: 24, fontSize: 12, color: '#8c8c8c' }}>
                                    Os usuários do portal com notificações ligadas recebem um e-mail com o link da pendência.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                title="Arquivar Dívida"
                visible={archiveModalVisible}
                onCancel={handleCloseArchiveModal}
                onOk={handleArchive}
                okText="Arquivar"
                cancelText="Cancelar"
            >
                <p style={{ marginBottom: 16 }}>
                    Você está arquivando a dívida do cliente:
                </p>
                {archiveRecord && archiveRecord.customer && (
                    <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                        <Tag>{archiveRecord.customer.cod}</Tag>
                        {archiveRecord.customer.name}
                        <div style={{ marginTop: 8 }}>
                            <Tag color={SCOPE_COLORS[archiveRecord.scope]}>{getScopeLabel(archiveRecord.scope)}</Tag>
                            {hasValue(archiveRecord.value) && (
                                <span style={{ marginLeft: 8 }}>{formatCurrency(archiveRecord.value)}</span>
                            )}
                        </div>
                    </div>
                )}
                <div>
                    <label>Motivo do arquivamento (opcional)</label>
                    <Input.TextArea
                        rows={3}
                        placeholder="Informe o motivo do arquivamento..."
                        value={archiveNote}
                        onChange={function(e) { setArchiveNote(e.target.value); }}
                    />
                </div>
            </Modal>
        </div>
    );
}

var mapStateToProps = function(state) {
    return {
        data: state.dividasTributarias.data,
        isLoading: state.dividasTributarias.isLoading,
        customers: (state.customers && state.customers.list) || []
    };
};

var mapDispatchProps = function(dispatch) {
    return {
        createDivida: function(data) { return dispatch(dividasActions.createDivida(data)); },
        updateDivida: function(id, data) { return dispatch(dividasActions.updateDivida(id, data)); },
        deleteDivida: function(id) { return dispatch(dividasActions.deleteDivida(id)); },
        archiveDivida: function(id, note) { return dispatch(dividasActions.archiveDivida(id, note)); },
        unarchiveDivida: function(id) { return dispatch(dividasActions.unarchiveDivida(id)); }
    };
};

export default connect(mapStateToProps, mapDispatchProps)(List);
